import { useState, useEffect, useCallback } from "react";
import { ScrollText, Shield, CheckCircle2 } from "lucide-react";
import { apiCall } from "../App";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CouponInput, CurrencySelector } from "./CouponCurrency";

const OrderCoachAssessmentModal = ({ open, onClose, token, user }) => {
  const [config, setConfig] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [, setCouponData] = useState(null);
  const [currency, setCurrency] = useState("INR");
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);

  // Pull a no-op CREATE with currency to fetch live pricing (no order is persisted because we
  // immediately retry on Pay). For a cleaner UX we hit the create endpoint which already returns
  // resolved base_price/gst/total for the requested currency. We DO need to handle the side-effect
  // that orders get created — so we only call this once on open & once on currency change.
  const fetchPricing = useCallback(async (curr, coupon = "") => {
    const res = await apiCall("POST", "/coach-assessment-orders/create", { currency: curr, coupon_code: coupon }, token);
    if (res.success) {
      setPricing(res.data);
      setConfig(prev => ({ ...(prev || {}), lut_active: curr === "USD" && res.data.gst_percent === 0 }));
    }
  }, [token]);

  useEffect(() => {
    if (!open) return;
    setCouponCode("");
    setCouponData(null);
    setCurrency("INR");
    fetchPricing("INR", "");
  }, [open, fetchPricing]);

  const onCurrencyChange = (c) => {
    setCurrency(c);
    setCouponCode("");
    setCouponData(null);
    fetchPricing(c, "");
  };

  const onCouponApply = async (code, data) => {
    setCouponCode(code);
    setCouponData(data);
    fetchPricing(currency, code);
  };
  const onCouponClear = () => {
    setCouponCode("");
    setCouponData(null);
    fetchPricing(currency, "");
  };

  const handleOrder = async () => {
    if (!pricing) return;
    setLoading(true);
    // Create a fresh order at click-time (pricing call may be stale)
    const createRes = await apiCall("POST", "/coach-assessment-orders/create", { currency, coupon_code: couponCode }, token);
    if (!createRes.success) {
      toast.error(createRes.error || "Failed to create order");
      setLoading(false);
      return;
    }
    const { order_id, razorpay_order_id, razorpay_key_id, total } = createRes.data;

    // Free order via 100% coupon — auto verify
    if (total <= 0) {
      const v = await apiCall("POST", "/coach-assessment-orders/verify", { order_id, razorpay_payment_id: "coupon_free" }, token);
      setLoading(false);
      if (v.success) { toast.success("Coach Assessment Matrix activated (free with coupon)."); onClose(true); }
      else { toast.error(v.error || "Verification failed"); onClose(false); }
      return;
    }

    if (!razorpay_order_id || !razorpay_key_id) {
      toast.error("Payment gateway not configured");
      setLoading(false);
      return;
    }

    const options = {
      key: razorpay_key_id,
      amount: total * 100,
      currency,
      name: "SIWON HARMONY",
      description: "Coach Assessment Matrix — Relationship Wisdom Coaching",
      order_id: razorpay_order_id,
      prefill: { name: user?.name || "", email: user?.email || "" },
      theme: { color: "#B8860B" },
      handler: async (response) => {
        const v = await apiCall("POST", "/coach-assessment-orders/verify",
          { order_id, razorpay_payment_id: response.razorpay_payment_id }, token);
        setLoading(false);
        if (v.success) {
          toast.success("Coach Assessment Matrix activated. Begin from your dashboard.");
          onClose(true);
        } else { toast.error(v.error || "Payment verification failed"); onClose(false); }
      },
      modal: { ondismiss: () => setLoading(false) },
    };
    const rz = new window.Razorpay(options);
    rz.open();
  };

  const sym = currency === "USD" ? "$" : "\u20B9";
  const basePrice = pricing?.base_price ?? 0;
  const discount = pricing?.discount ?? 0;
  const gstPct = pricing?.gst_percent ?? 0;
  const gstAmount = pricing?.gst_amount ?? 0;
  const total = pricing?.total ?? 0;
  const gstLabel = currency === "USD" ? "IGST" : "GST";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-w-md bg-cosmic-void border border-divine-gold/20" data-testid="order-coach-assessment-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-divine-gold">
            <ScrollText className="w-5 h-5" /> Order Coach Assessment Matrix
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          <p className="text-chakra-crown/70 text-sm leading-relaxed">
            A premium <strong className="text-divine-gold">Cosmic Foundation Diagnostic</strong> across
            <strong className="text-divine-gold"> 5 pillars and 30 dimensions</strong> — Shiva, Shakti,
            Gunas, Arishadvargas, and Pañca Koshas. You complete it first; your coach then reviews
            and refines your responses.
          </p>

          <div className="p-4 rounded-xl bg-cosmic-light/5 border border-divine-gold/10">
            <p className="text-divine-gold text-xs font-heading tracking-wider mb-3">WHAT YOU GET</p>
            {[
              "Comprehensive 30-dimension Vedic diagnostic",
              "Sub-scoring across all 5 cosmic pillars",
              "Coach-reviewed assessment + recommendations",
              "Personalized practice prescriptions",
              "Integrated within your Awareness → Alignment → Awakening flow",
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2 mb-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                <span className="text-chakra-crown/70 text-xs">{item}</span>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <CurrencySelector value={currency} onChange={onCurrencyChange} />
            <CouponInput
              token={token}
              module="relationship"
              currency={currency}
              orderType="coach_assessment"
              onApply={onCouponApply}
              onClear={onCouponClear}
            />
          </div>

          <div className="p-4 rounded-xl bg-divine-gold/5 border border-divine-gold/20">
            <div className="flex justify-between text-sm text-chakra-crown/70 mb-1">
              <span>Coach Assessment Matrix</span>
              <span>{sym}{basePrice}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between text-sm text-emerald-400 mb-1">
                <span>Discount ({couponCode})</span>
                <span>-{sym}{discount}</span>
              </div>
            )}
            {gstPct > 0 && (
              <div className="flex justify-between text-sm text-chakra-crown/50 mb-2">
                <span>{gstLabel} ({gstPct}%)</span>
                <span>{sym}{gstAmount}</span>
              </div>
            )}
            {currency === "USD" && config?.lut_active && (
              <div className="flex justify-between text-xs text-emerald-400/80 mb-2">
                <span>Export under LUT — IGST zero-rated</span>
                <span>—</span>
              </div>
            )}
            <div className="border-t border-divine-gold/20 pt-2 flex justify-between text-base font-bold text-divine-gold">
              <span>Total</span>
              <span>{sym}{total}</span>
            </div>
          </div>

          <Button
            onClick={handleOrder}
            disabled={loading || !pricing}
            data-testid="order-coach-assessment-pay-btn"
            className="w-full bg-divine-gold hover:bg-divine-gold/90 text-cosmic-void font-heading text-base py-6"
          >
            {loading ? "Processing..." : `Pay ${currency} ${total} & Activate`}
          </Button>

          <div className="flex items-center justify-center gap-1.5 text-chakra-crown/40 text-[10px]">
            <Shield className="w-3 h-3" /> Secured by Razorpay
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrderCoachAssessmentModal;
